import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import { isToday } from "date-fns";

interface ChatBubbleProps {
  className?: string;
}

const SUPPORT_MESSAGES = [
  "You might be going through a lot right now, you can talk to me",
  "I'm here if you'd like to talk about how you're feeling.",
  "If things feel heavy, you don't have to keep it in — I'm here to listen.",
  "Whenever you're ready, you can share what's on your mind.",
  "It's okay if today feels tough — we can talk through it together.",
];

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export function ChatBubble({ className = "" }: ChatBubbleProps) {
  // Persist chat state across page navigation
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem("chatBubbleOpen");
    return saved ? JSON.parse(saved) : false;
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi there! I'm here to listen and support you. How are you feeling today?",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [animatingMessages, setAnimatingMessages] = useState<Set<string>>(new Set());
  const [allMessagesLoaded, setAllMessagesLoaded] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const apiClient = useApi();

  // Persist chat open state
  useEffect(() => {
    localStorage.setItem("chatBubbleOpen", JSON.stringify(isOpen));
  }, [isOpen]);

  // Fetch today's check-in data to check mood score
  const { data: checkInsData } = useQuery({
    queryKey: ["checkins", { limit: 1 }], // Get latest check-in
    queryFn: () => apiClient.get("/api/v1/checkins?limit=1"),
  });

  // Load conversation history when chat opens
  const { data: conversationsData } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => apiClient.get("/api/v1/chat/conversations?status=active&limit=1"),
    enabled: isOpen && !hasLoadedHistory,
  });

  // Load messages for the current conversation
  const { data: messagesData } = useQuery({
    queryKey: ["chat-messages", currentConversationId],
    queryFn: () => apiClient.get(`/api/v1/chat/messages?conversationId=${currentConversationId}`),
    enabled: !!currentConversationId && isOpen,
  });

  // Chat API mutation
  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await apiClient.post("/api/v1/ai/chat", {
        message,
        conversationId: currentConversationId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Add to animating messages for fade-in-up effect
      setAnimatingMessages((prev) => new Set(prev).add(assistantMessage.id));

      // Update conversation ID if we got one
      if (data.conversationId && !currentConversationId) {
        setCurrentConversationId(data.conversationId);
      }

      setIsTyping(false);
    },
    onError: (error) => {
      console.error("Chat error:", error);
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsTyping(false);
    },
  });

  // Check if today's mood score is low (< 5)
  const todaysCheckIn = (checkInsData as any)?.data?.checkIns?.[0];
  const isTodaysCheckIn = todaysCheckIn && isToday(new Date(todaysCheckIn.timestamp));
  const hasLowMoodScore = isTodaysCheckIn && todaysCheckIn.moodScore && todaysCheckIn.moodScore < 5;

  // Load conversation history when chat opens
  useEffect(() => {
    if (isOpen && conversationsData && !hasLoadedHistory) {
      const conversations = (conversationsData as any)?.data?.conversations || [];
      if (conversations.length > 0) {
        const latestConversation = conversations[0];
        setCurrentConversationId(latestConversation.id);
      }
      setHasLoadedHistory(true);
    }
  }, [isOpen, conversationsData, hasLoadedHistory]);

  // Load messages when conversation is set - only latest user and assistant messages
  useEffect(() => {
    if (messagesData && currentConversationId) {
      const dbMessages = (messagesData as any)?.data?.messages || [];
      if (dbMessages.length > 0) {
        // Sort messages by created_at to find latest ones
        const sortedMessages = dbMessages.sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Find the latest user and assistant messages
        const latestUserMessage = [...sortedMessages]
          .reverse()
          .find((msg: any) => msg.role === "user");
        const latestAssistantMessage = [...sortedMessages]
          .reverse()
          .find((msg: any) => msg.role === "assistant");

        const latestMessages: ChatMessage[] = [];

        if (latestUserMessage) {
          latestMessages.push({
            id: latestUserMessage.id,
            role: latestUserMessage.role,
            content: latestUserMessage.content,
            timestamp: new Date(latestUserMessage.created_at),
          });
        }

        if (latestAssistantMessage) {
          latestMessages.push({
            id: latestAssistantMessage.id,
            role: latestAssistantMessage.role,
            content: latestAssistantMessage.content,
            timestamp: new Date(latestAssistantMessage.created_at),
          });
        }

        // Sort by timestamp for chronological order
        latestMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        setMessages(latestMessages);

        // Animate the loaded messages
        const messageIds = new Set(latestMessages.map((msg) => msg.id));
        setAnimatingMessages(messageIds);

        // Reset loading states
        setAllMessagesLoaded(false);
        setIsLoadingOlder(false);
      }
    }
  }, [messagesData, currentConversationId]);

  // Load older messages when scrolling up
  const loadOlderMessages = async () => {
    if (!currentConversationId || allMessagesLoaded || isLoadingOlder) return;

    setIsLoadingOlder(true);
    try {
      // Get all messages for this conversation to find older ones
      const response = await apiClient.get(
        `/api/v1/chat/messages?conversationId=${currentConversationId}`
      );
      const allDbMessages = (response as any)?.data?.messages || [];

      if (allDbMessages.length > 0) {
        // Sort all messages by created_at
        const sortedAllMessages = allDbMessages.sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Find messages that are older than our current oldest message
        const currentOldestTimestamp = Math.min(...messages.map((m) => m.timestamp.getTime()));
        const olderMessages = sortedAllMessages.filter(
          (msg: any) => new Date(msg.created_at).getTime() < currentOldestTimestamp
        );

        if (olderMessages.length > 0) {
          // Take the most recent batch of older messages (last 10)
          const messagesToAdd = olderMessages.slice(-10).map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }));

          // Prepend older messages
          setMessages((prevMessages) => [...messagesToAdd, ...prevMessages]);

          // Animate the newly loaded messages
          const newMessageIds = new Set(messagesToAdd.map((msg) => msg.id));
          setAnimatingMessages((prev) => new Set([...prev, ...newMessageIds]));
        } else {
          setAllMessagesLoaded(true);
        }
      }
    } catch (error) {
      console.error("Failed to load older messages:", error);
    } finally {
      setIsLoadingOlder(false);
    }
  };

  // Handle scroll to load older messages
  const handleScroll = useCallback(
    (event: Event) => {
      const container = event.target as HTMLElement;
      if (container && container.scrollTop < 100 && !isLoadingOlder && !allMessagesLoaded) {
        loadOlderMessages();
      }
    },
    [isLoadingOlder, allMessagesLoaded]
  );

  // Add scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [currentConversationId, allMessagesLoaded, isLoadingOlder, handleScroll]);

  // Reset state when chat closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([
        {
          id: "welcome",
          role: "assistant",
          content: "Hi there! I'm here to listen and support you. How are you feeling today?",
          timestamp: new Date(),
        },
      ]);
      setCurrentConversationId(null);
      setHasLoadedHistory(false);
      setAllMessagesLoaded(false);
      setIsLoadingOlder(false);
    }
  }, [isOpen]);

  // Gentle fade-in animation instead of auto-scroll for better UX in mental health context
  // Messages appear with fade-in-up animation rather than jarring scroll

  // Show tooltip and shake animation when mood is low
  useEffect(() => {
    if (hasLowMoodScore && !isOpen) {
      // Show tooltip after a short delay
      const tooltipTimer = setTimeout(() => {
        setShowTooltip(true);
      }, 1000);

      return () => {
        clearTimeout(tooltipTimer);
      };
    } else {
      setShowTooltip(false);
    }
  }, [hasLowMoodScore, isOpen]);

  // Rotate through support messages every 8 seconds when tooltip is shown
  useEffect(() => {
    if (showTooltip) {
      const messageTimer = setInterval(() => {
        setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % SUPPORT_MESSAGES.length);
      }, 8000); // Change message every 8 seconds

      return () => clearInterval(messageTimer);
    }
  }, [showTooltip]);

  // Send message function
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    // Add user message to local state immediately for better UX
    setMessages((prev) => [...prev, userMessage]);

    // Add to animating messages for fade-in-up effect
    setAnimatingMessages((prev) => new Set(prev).add(userMessage.id));

    setInputMessage("");
    setIsTyping(true);

    // Send to AI (which will save to database and return assistant response)
    chatMutation.mutate(inputMessage.trim());
  };

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Bubble */}
      <div className={`fixed bottom-6 right-6 z-40 ${className}`}>
        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute bottom-full right-0 mb-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg w-80 animate-fade-in">
            <div className="relative">
              <p className="text-center leading-relaxed">{SUPPORT_MESSAGES[currentMessageIndex]}</p>
              {/* Arrow */}
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}

        {/* Chat Button - Hide only when popup is shown */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className={`
              w-14 h-14 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg
              flex items-center justify-center transition-all duration-200
            `}
            aria-label="Open chat"
          >
            <MessageCircle className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Chat Modal - Replaces button at same location */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 pointer-events-none">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-80 max-h-[60vh] flex flex-col pointer-events-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Chat Support</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
            >
              {/* Loading older messages indicator */}
              {isLoadingOlder && (
                <div className="flex justify-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                </div>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} ${
                    animatingMessages.has(message.id) ? "animate-fade-in-up" : ""
                  }`}
                  onAnimationEnd={() => {
                    // Remove from animating messages after animation completes
                    setAnimatingMessages((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(message.id);
                      return newSet;
                    });
                  }}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.role === "user"
                        ? "bg-red-600 text-white rounded-br-none"
                        : "bg-gray-100 text-gray-900 rounded-bl-none"
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    <p
                      className={`text-xs mt-1 ${
                        message.role === "user" ? "text-red-100" : "text-gray-500"
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-none px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Disclaimer */}
            <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>Support:</strong> This chat provides emotional support. For mental health
                crises, contact professionals or emergency services.
              </p>
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={2}
                  disabled={isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  className="self-end bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
